import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/store/auth-store'

interface QueuedNotification {
  id: string
  user_id: string
  title: string
  body: string
  type: string
  url?: string
  data?: Record<string, any>
  created_at: string
}

/**
 * Hook que processa notificações pendentes da fila em tempo real
 * 
 * Este hook:
 * 1. Escuta novas notificações na fila via Supabase Realtime
 * 2. Envia notificações push via Service Worker
 * 3. Marca como entregue no banco
 */
export const useNotificationQueueProcessor = () => {
  const { user } = useAuthStore()
  const processingRef = useRef<Set<string>>(new Set())

  console.log('🔔 [Notification Queue] Hook chamado, user:', user?.id)

  useEffect(() => {
    console.log('🔔 [Notification Queue] useEffect disparado, user:', user?.id)
    
    if (!user?.id) {
      console.log('❌ [Notification Queue] Sem usuário, saindo...')
      return
    }

    console.log('🔔 [Notification Queue] Iniciando processador de notificações')

    // Processar notificações pendentes ao iniciar
    processExistingQueue()

    // Escutar novas notificações via Realtime
    const channel = supabase
      .channel(`notification_queue:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_queue',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('🆕 [Notification Queue] Nova notificação:', payload.new)
          processNotification(payload.new as QueuedNotification)
        }
      )
      .subscribe()

    return () => {
      console.log('🔕 [Notification Queue] Parando processador')
      supabase.removeChannel(channel)
    }
  }, [user?.id])

  const processExistingQueue = async () => {
    if (!user?.id) return

    try {
      console.log('📥 [Notification Queue] Processando fila existente...')

      // MUDANÇA: Só processa notificações dos últimos 5 minutos
      // Evita reprocessar notificações antigas ao recarregar
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString()

      const { data: pendingNotifications, error } = await supabase
        .from('notification_queue')
        .select('*')
        .eq('user_id', user.id)
        .eq('delivered', false)
        .is('processed_at', null)
        .lt('retry_count', 3)
        .gte('created_at', fiveMinutesAgo) // Só notificações recentes
        .order('created_at', { ascending: true })
        .limit(10)

      if (error) {
        console.error('❌ [Notification Queue] Erro ao buscar fila:', error)
        throw error
      }

      if (pendingNotifications && pendingNotifications.length > 0) {
        console.log(`📋 [Notification Queue] ${pendingNotifications.length} notificações pendentes`)
        
        for (const notification of pendingNotifications) {
          await processNotification(notification as QueuedNotification)
          // Pequeno delay entre notificações
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      } else {
        console.log('✅ [Notification Queue] Nenhuma notificação pendente')
      }
    } catch (error) {
      console.error('❌ [Notification Queue] Erro ao processar fila:', error)
    }
  }

  const processNotification = async (notification: QueuedNotification) => {
    // Evitar processar a mesma notificação múltiplas vezes
    if (processingRef.current.has(notification.id)) {
      console.log(`⏭️ [Notification Queue] Notificação ${notification.id} já está sendo processada`)
      return
    }

    processingRef.current.add(notification.id)

    try {
      console.log(`📤 [Notification Queue] Processando notificação ${notification.id}`)

      // Verificar se o Service Worker está disponível
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker não suportado')
      }

      const registration = await navigator.serviceWorker.ready

      // Mostrar notificação via Service Worker
      await registration.showNotification(notification.title, {
        body: notification.body,
        icon: '/images/logos/icon-192x192.png',
        badge: '/images/logos/favicon.png',
        tag: notification.type,
        data: {
          url: notification.url || '/',
          ...notification.data,
        },
        vibrate: [200, 100, 200],
        requireInteraction: notification.type === 'trade_request',
      })

      console.log(`✅ [Notification Queue] Notificação ${notification.id} enviada`)

      // Marcar como entregue
      const { error: updateError } = await supabase
        .from('notification_queue')
        .update({
          delivered: true,
          processed_at: new Date().toISOString(),
        })
        .eq('id', notification.id)

      if (updateError) {
        console.error(`❌ [Notification Queue] Erro ao marcar como entregue:`, updateError)
      } else {
        console.log(`✅ [Notification Queue] Notificação ${notification.id} marcada como entregue`)
      }

      // Também logar na tabela antiga para compatibilidade
      await supabase.from('push_notifications_log').insert({
        user_id: notification.user_id,
        title: notification.title,
        body: notification.body,
        type: notification.type,
        data: notification.data,
        delivered: true,
      })

    } catch (error: any) {
      console.error(`❌ [Notification Queue] Erro ao processar ${notification.id}:`, error)

      // Incrementar contador de tentativas
      await supabase
        .from('notification_queue')
        .update({
          retry_count: notification.retry_count + 1,
          error: error.message,
          processed_at: new Date().toISOString(),
        })
        .eq('id', notification.id)
    } finally {
      processingRef.current.delete(notification.id)
    }
  }
}

